import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users, Music, Calendar, ArrowLeft } from 'lucide-react';

const UserProfile = ({ currentUser }) => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activity, setActivity] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Check if viewing own profile or is admin
  const canSeeEmail = currentUser && (currentUser.id === userId || currentUser.role === 'admin');

  useEffect(() => {
    fetchUserProfile();
    checkFollowStatus();
  }, [userId]);

  const fetchUserProfile = async () => {
    try {
      // Fetch user by ID
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}`
      );
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        fetchUserActivity(userId);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserActivity = async (uid) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${uid}/activity`
      );
      if (response.ok) {
        const data = await response.json();
        setActivity(data);
      }
    } catch (error) {
      console.error('Failed to fetch activity:', error);
    }
  };

  const checkFollowStatus = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/is-following`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setIsFollowing(data.is_following);
      }
    } catch (error) {
      console.error('Failed to check follow status:', error);
    }
  };

  const toggleFollow = async () => {
    try {
      const method = isFollowing ? 'DELETE' : 'POST';
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/follow`,
        {
          method,
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );
      
      if (response.ok) {
        setIsFollowing(!isFollowing);
      }
    } catch (error) {
      console.error('Failed to toggle follow:', error);
    }
  };

  if (loading) {
    return <div className="page-content">Loading...</div>;
  }

  if (!user) {
    return <div className="page-content">User not found</div>;
  }

  return (
    <div className="page-content">
      <button className="back-button" onClick={() => navigate(-1)}>
        <ArrowLeft size={20} />
        Back
      </button>

      <div className="user-profile-header">
        <div className="user-avatar">
          {user.name.charAt(0).toUpperCase()}
        </div>
        <div className="user-info">
          <h1>{user.name}</h1>
          {canSeeEmail && <p className="user-email">{user.email}</p>}
          <div className="user-stats">
            <div className="stat">
              <Calendar size={16} />
              Joined {new Date(user.created_at).toLocaleDateString()}
            </div>
          </div>
        </div>
        <button 
          className={`follow-button ${isFollowing ? 'following' : ''}`}
          onClick={toggleFollow}
        >
          <Users size={16} />
          {isFollowing ? 'Following' : 'Follow'}
        </button>
      </div>

      <div className="user-activity-section">
        <h2>
          <Music size={20} />
          Recent Activity
        </h2>
        {activity.length === 0 ? (
          <div className="empty-state">
            {user.name} hasn't shared any listening activity yet
          </div>
        ) : (
          <div className="activity-list">
            {activity.map((item, index) => (
              <div key={index} className="activity-item">
                <Music size={20} />
                <div>
                  Listened to <span className="song-name">{item.song_title}</span> by {item.artist}
                  <div className="activity-time">
                    {new Date(item.listened_at).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
